// ============================================================
// SIMADCA v3.0 – AuthContext FINAL
// Persistență localStorage, preferințe notificări, activitate
// ============================================================

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { AppUser, UserActivity } from '../types';

const LS_USER_KEY     = 'simadca_user_v3';
const LS_USERS_KEY    = 'simadca_users_v3';
const LS_ACTIVITY_KEY = 'simadca_activity_v3';

// ------ Utilizatori mock inițiali ------
const DEFAULT_USERS: (AppUser & { password: string })[] = [
  {
    id: 'u1', name: 'Administrator SIMADCA', email: 'admin@simadca.ro',
    password: 'admin123', role: 'admin', active: true,
    lastLogin: new Date().toISOString(),
    notifEmail: true, notifSms: false, notifBrowser: true, notifDailyReport: true,
    phone: '+40 740 339 467',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=Admin',
  },
  {
    id: 'u2', name: 'Ion Popescu', email: 'operator@simadca.ro',
    password: 'operator123', role: 'operator', active: true,
    lastLogin: new Date(Date.now() - 3600000).toISOString(),
    notifEmail: true, notifSms: true, notifBrowser: true, notifDailyReport: false,
    phone: '+40 720 000 001',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=Ion',
  },
  {
    id: 'u3', name: 'Maria Ionescu', email: 'viewer@simadca.ro',
    password: 'viewer123', role: 'viewer', active: true,
    lastLogin: new Date(Date.now() - 86400000).toISOString(),
    notifEmail: false, notifSms: false, notifBrowser: true, notifDailyReport: true,
    phone: '+40 730 000 002',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=Maria',
  },
  {
    id: 'u4', name: 'Gheorghe Marin', email: 'gmarin@simadca.ro',
    password: 'operator123', role: 'operator', active: true,
    lastLogin: new Date(Date.now() - 7200000).toISOString(),
    notifEmail: true, notifSms: false, notifBrowser: true, notifDailyReport: true,
    phone: '+40 750 000 003',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=Gheorghe',
  },
  {
    id: 'u5', name: 'Elena Constantin', email: 'econstantin@simadca.ro',
    password: 'viewer123', role: 'viewer', active: true,
    lastLogin: new Date(Date.now() - 172800000).toISOString(),
    notifEmail: true, notifSms: false, notifBrowser: false, notifDailyReport: true,
    phone: '+40 760 000 004',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=Elena',
  },
  {
    id: 'u6', name: 'Andrei Dumitrescu', email: 'adumitrescu@simadca.ro',
    password: 'operator123', role: 'operator', active: false,
    lastLogin: new Date(Date.now() - 604800000).toISOString(),
    notifEmail: false, notifSms: false, notifBrowser: false, notifDailyReport: false,
    phone: '+40 770 000 005',
    avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=Andrei',
  },
];

// ------ Activitate mock inițială ------
const DEFAULT_ACTIVITY: UserActivity[] = [
  { id: 'a1', userId: 'u1', action: 'Autentificare reușită',     section: 'Auth',        timestamp: new Date().toISOString() },
  { id: 'a2', userId: 'u1', action: 'Export raport PDF',          section: 'Rapoarte',    timestamp: new Date(Date.now() - 7200000).toISOString() },
  { id: 'a3', userId: 'u1', action: 'Alertă recunoscută (ACK)',   section: 'Alerte',      timestamp: new Date(Date.now() - 10800000).toISOString() },
  { id: 'a4', userId: 'u1', action: 'Configurare senzor FO-SPR',  section: 'Configurare', timestamp: new Date(Date.now() - 86400000).toISOString() },
  { id: 'a5', userId: 'u1', action: 'Raport zilnic vizualizat',   section: 'Rapoarte',    timestamp: new Date(Date.now() - 90000000).toISOString() },
  { id: 'a6', userId: 'u2', action: 'Autentificare reușită',      section: 'Auth',        timestamp: new Date(Date.now() - 3600000).toISOString() },
  { id: 'a7', userId: 'u2', action: 'Export CSV senzori',         section: 'Senzori',     timestamp: new Date(Date.now() - 5400000).toISOString() },
];

// ------ Helpers localStorage ------
const loadUsers = (): (AppUser & { password: string })[] => {
  try {
    const s = localStorage.getItem(LS_USERS_KEY);
    return s ? JSON.parse(s) : DEFAULT_USERS;
  } catch { return DEFAULT_USERS; }
};

const saveUsers = (users: (AppUser & { password: string })[]) => {
  try { localStorage.setItem(LS_USERS_KEY, JSON.stringify(users)); } catch {}
};

const loadCurrentUser = (): AppUser | null => {
  try {
    const s = localStorage.getItem(LS_USER_KEY);
    return s ? JSON.parse(s) : null;
  } catch { return null; }
};

const saveCurrentUser = (user: AppUser | null) => {
  try {
    if (user) localStorage.setItem(LS_USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(LS_USER_KEY);
  } catch {}
};

const loadActivity = (): UserActivity[] => {
  try {
    const s = localStorage.getItem(LS_ACTIVITY_KEY);
    return s ? JSON.parse(s) : DEFAULT_ACTIVITY;
  } catch { return DEFAULT_ACTIVITY; }
};

const saveActivity = (activity: UserActivity[]) => {
  try { localStorage.setItem(LS_ACTIVITY_KEY, JSON.stringify(activity.slice(0, 100))); } catch {}
};

// ------ Interfața contextului ------
interface AuthContextType {
  user: AppUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login:      (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout:     () => void;
  allUsers:   AppUser[];
  addUser:    (user: Omit<AppUser, 'id'> & { password: string }) => void;
  updateUser: (id: string, updates: Partial<AppUser & { password?: string }>) => void;
  deleteUser: (id: string) => void;
  logActivity:(action: string, section: string, details?: string) => void;
  getActivity:(userId?: string) => UserActivity[];
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [users,    setUsers]    = useState<(AppUser & { password: string })[]>(loadUsers);
  const [user,     setUser]     = useState<AppUser | null>(loadCurrentUser);
  const [isLoading, setIsLoading] = useState(false);
  const [activity, setActivity] = useState<UserActivity[]>(loadActivity);

  // Sync users to localStorage
  useEffect(() => { saveUsers(users); }, [users]);
  // Sync activity to localStorage
  useEffect(() => { saveActivity(activity); }, [activity]);

  const logActivity = useCallback((action: string, section: string, details?: string) => {
    if (!user) return;
    const entry: UserActivity = {
      id: `act-${Date.now()}`,
      userId: user.id,
      action, section, details,
      timestamp: new Date().toISOString(),
    };
    setActivity(prev => [entry, ...prev].slice(0, 100));
  }, [user]);

  const getActivity = useCallback((userId?: string): UserActivity[] => {
    if (!userId) return activity;
    return activity.filter(a => a.userId === userId);
  }, [activity]);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 600));

    const found = users.find(
      u => u.email.toLowerCase() === email.toLowerCase() && u.password === password
    );

    if (!found) {
      setIsLoading(false);
      return { success: false, error: 'Email sau parolă incorectă.' };
    }
    if (!found.active) {
      setIsLoading(false);
      return { success: false, error: 'Contul este dezactivat. Contactați administratorul.' };
    }

    const { password: _pw, ...safeUser } = found;
    const loggedUser = { ...safeUser, lastLogin: new Date().toISOString() };

    // Update lastLogin in users list
    setUsers(prev => prev.map(u => u.id === loggedUser.id ? { ...u, lastLogin: loggedUser.lastLogin } : u));
    setUser(loggedUser);
    saveCurrentUser(loggedUser);

    // Log activitate
    const entry: UserActivity = {
      id: `act-${Date.now()}`,
      userId: loggedUser.id,
      action: 'Autentificare reușită',
      section: 'Auth',
      timestamp: new Date().toISOString(),
    };
    setActivity(prev => [entry, ...prev].slice(0, 100));

    setIsLoading(false);
    return { success: true };
  };

  const logout = useCallback(() => {
    if (user) {
      const entry: UserActivity = {
        id: `act-${Date.now()}`,
        userId: user.id,
        action: 'Deconectare',
        section: 'Auth',
        timestamp: new Date().toISOString(),
      };
      setActivity(prev => [entry, ...prev].slice(0, 100));
    }
    setUser(null);
    saveCurrentUser(null);
  }, [user]);

  const allUsers: AppUser[] = users.map(({ password: _pw, ...u }) => u);

  const addUser = useCallback((newUser: Omit<AppUser, 'id'> & { password: string }) => {
    const id = `u${Date.now()}`;
    const full = {
      id,
      name:             newUser.name,
      email:            newUser.email,
      password:         newUser.password,
      role:             newUser.role,
      active:           newUser.active ?? true,
      lastLogin:        undefined,
      phone:            newUser.phone,
      notifEmail:       newUser.notifEmail ?? true,
      notifSms:         newUser.notifSms ?? false,
      notifBrowser:     newUser.notifBrowser ?? true,
      notifDailyReport: newUser.notifDailyReport ?? true,
      avatar: `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(newUser.name)}`,
    };
    setUsers(prev => [...prev, full]);
    logActivity(`Utilizator adăugat: ${newUser.name}`, 'Utilizatori');
  }, [logActivity]);

  const updateUser = useCallback((id: string, updates: Partial<AppUser & { password?: string }>) => {
    setUsers(prev => prev.map(u => u.id === id ? { ...u, ...updates } : u));
    if (user?.id === id) {
      const { password: _pw, ...safeUpdates } = updates as any;
      const updated = { ...user, ...safeUpdates };
      setUser(updated);
      saveCurrentUser(updated);
    }
    logActivity(`Utilizator actualizat: ${id}`, 'Utilizatori');
  }, [user, logActivity]);

  const deleteUser = useCallback((id: string) => {
    const target = users.find(u => u.id === id);
    setUsers(prev => prev.filter(u => u.id !== id));
    logActivity(`Utilizator șters: ${target?.name ?? id}`, 'Utilizatori');
  }, [users, logActivity]);

  return (
    <AuthContext.Provider value={{
      user, isAuthenticated: !!user, isLoading,
      login, logout, allUsers,
      addUser, updateUser, deleteUser,
      logActivity, getActivity,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth trebuie folosit în interiorul AuthProvider');
  return ctx;
};